import type { FocusTrap } from 'focus-trap';
import fastdom from 'fastdom';
import { hide as hideOverlay, show as showOverlay, Options as OverlayOptions } from 'utils/overlay';
import transitionEnd from 'utils/transition-end';
import repaint from 'utils/repaint';

type Events = 'open' | 'opened' | 'close' | 'closed';

type CloseOptions = {
  escape: boolean;
  clickOutside: boolean;
};

type Options = {
  close: CloseOptions | false;
  overlay: OverlayOptions | false;
};

export type OptionsConstructor = Partial<{
  close: boolean | Partial<Options['close']>;
  overlay: boolean | Partial<Options['overlay']>;
}>;

export default class {
  public readonly root: HTMLElement;

  private isActive: boolean;

  private focusTrap: FocusTrap;

  private options: Options;

  constructor(element: HTMLElement, options?: OptionsConstructor) {
    this.setProps(options);

    this.root = element;

    this.isActive = false;
    this.root.hidden = true;

    this.clickEvent = this.clickEvent.bind(this);
    this.keydownEvent = this.keydownEvent.bind(this);
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.toggle = this.toggle.bind(this);

    this.handleEvents();
  }

  // eslint-disable-next-line class-methods-use-this
  protected getActiveClass(): string {
    throw new Error('There must exist an `getActiveClass` method in final class');
  }

  private setProps(options?: OptionsConstructor): void {
    this.options = {
      close: {
        escape: true,
        clickOutside: true,
      },

      overlay: {
        index: null,
      },

      ...options,

      // Close settings
      ...(options && options.close === true
        ? {
            close: {
              escape: true,
              clickOutside: true,
            },
          }
        : undefined),

      // Overlay settings
      ...(options && options.overlay === true
        ? {
            overlay: {
              index: null,
            },
          }
        : undefined),
    };
  }

  private handleEvents(): void {
    this.on('open', () => {
      if (this.options.close) {
        if (this.options.close.clickOutside) {
          document.addEventListener('touchstart', this.clickEvent);
          document.addEventListener('click', this.clickEvent);
        }

        if (this.options.close.escape) {
          document.addEventListener('keydown', this.keydownEvent);
        }
      }
    });

    this.on('open', async () => {
      if (!this.focusTrap) {
        const { createFocusTrap } = await import(/* webpackChunkName: "focus-trap" */ 'focus-trap');

        this.focusTrap = createFocusTrap(this.root, {
          allowOutsideClick: true,
        });
      }

      if (this.isActive) {
        this.focusTrap.activate();
      }
    });

    this.on('close', () => {
      this.focusTrap?.deactivate();

      if (this.options.close) {
        if (this.options.close.clickOutside) {
          document.removeEventListener('touchstart', this.clickEvent);
          document.removeEventListener('click', this.clickEvent);
        }

        if (this.options.close.escape) {
          document.removeEventListener('keydown', this.keydownEvent);
        }
      }
    });
  }

  private clickEvent(event: MouseEvent | TouchEvent): void {
    if (!(event.target instanceof Node)) {
      return;
    }

    if (
      !this.root.contains(event.target) &&
      !(event.target as HTMLElement).hasAttribute('data-club-signup') &&
      !(event.target as HTMLElement).closest('[data-club-signup]')
    ) {
      event.stopPropagation();

      this.close();
    }
  }

  private keydownEvent(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  private dispatchEvent(type: Events): void {
    const event = new Event(type);

    this.root.dispatchEvent(event);
  }

  toggle(): void {
    if (this.isActive) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    if (this.isActive) {
      return;
    }

    this.isActive = true;

    fastdom.measure(() => {
      this.root.hidden = false;
      repaint(this.root);
    });

    fastdom.mutate(() => {
      this.dispatchEvent('open');
      this.root.classList.add(this.getActiveClass());

      transitionEnd(this.root, () => {
        if (this.isActive) {
          this.dispatchEvent('opened');
        }
      });
    });

    if (this.options.overlay) {
      showOverlay(this.options.overlay);
    }
  }

  close(): void {
    if (!this.isActive) {
      return;
    }

    this.isActive = false;

    fastdom.mutate(() => {
      this.dispatchEvent('close');
      this.root.classList.remove(this.getActiveClass());

      transitionEnd(this.root, () => {
        if (!this.isActive) {
          this.root.hidden = true;
          this.dispatchEvent('closed');
        }
      });
    });

    if (this.options.overlay) {
      hideOverlay();
    }
  }

  on(type: Events, callback: CallableFunction, options?: boolean | AddEventListenerOptions): void {
    this.root.addEventListener(type, (event) => callback(event), options);
  }
}
